import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CloudArrowUpIcon, DocumentIcon, XMarkIcon, CheckCircleIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { uploadApi, reportApi } from '../lib/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTeamId } from '../hooks/useTeamId';
import ActiveBankNotice from '../components/ActiveBankNotice';

export default function UploadPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [batchName, setBatchName] = useState('');
  // Optional landing-page URL: when set, the compliance scan post-processes
  // disclosure findings against this page, marking any required disclosure
  // present on the landing page as "satisfied" rather than violation. Backs
  // the "click + scroll away" disclosure interpretation.
  const [landingPageUrl, setLandingPageUrl] = useState('');

  // Bind to the ACTIVE team rather than a per-feature picker (no wrong-bank footgun).
  const { teamId: activeTeamId } = useTeamId();

  // Fetch recent reports with auto-refresh for pending/processing
  const { data: recentReports, refetch: refetchReports } = useQuery({
    queryKey: ['recentReports'],
    queryFn: async () => {
      const response = await reportApi.getReports({ limit: 10 });
      return response.data;
    },
    refetchInterval: (query) => {
      // Auto-refresh every 10 seconds if any reports are pending/processing
      const data = query.state.data;
      const hasPending = data?.reports?.some((r: any) =>
        r.status === 'PENDING' || r.status === 'PROCESSING'
      );
      return hasPending ? 10000 : false;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      const trimmedUrl = landingPageUrl.trim() || undefined;
      if (files.length === 1) {
        return uploadApi.uploadSingle(files[0], activeTeamId || undefined, trimmedUrl);
      } else {
        return uploadApi.uploadBatch(
          files,
          activeTeamId || undefined,
          batchName || undefined,
          trimmedUrl
        );
      }
    },
    onSuccess: () => {
      toast.success(`${files.length} file(s) uploaded successfully! Processing compliance checks...`);
      setFiles([]);
      setBatchName('');
      setLandingPageUrl('');
      refetchReports(); // Refresh the recent reports list

      // Stay on upload page - users can navigate to reports to see results
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Upload failed');
    },
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
    },
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Active team must be resolved before uploading (only false briefly while loading).
  const needsTeamSelection = !activeTeamId;

  const handleUpload = () => {
    if (files.length === 0) {
      toast.error('Please select at least one file');
      return;
    }
    if (needsTeamSelection) {
      toast.error('Please select a team before uploading');
      return;
    }
    uploadMutation.mutate();
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
      case 'PROCESSING':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
            <ArrowPathIcon className="h-3 w-3 animate-spin" />
            {status === 'PENDING' ? 'Pending' : 'Processing'}
          </span>
        );
      case 'PASSED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
            <CheckCircleIcon className="h-3 w-3" />
            No Issues
          </span>
        );
      case 'FAILED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
            <XCircleIcon className="h-3 w-3" />
            Flagged for Review
          </span>
        );
      case 'ERROR':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
            <XCircleIcon className="h-3 w-3" />
            Processing Error
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Upload Ad Creative</h1>
        <p className="mt-2 text-gray-600">
          Upload your advertising materials for FDIC/NCUA compliance checking
        </p>
      </div>

      {/* Active bank — content is reviewed under the active tenant (switch via the sidebar). */}
      <ActiveBankNotice className="mb-6" />

      {/* Landing page URL (optional) */}
      {!!activeTeamId && (
        <div className="mb-6">
          <label htmlFor="landing-page-url" className="block text-sm font-medium text-gray-700 mb-1">
            Landing page URL <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id="landing-page-url"
            type="url"
            value={landingPageUrl}
            onChange={(e) => setLandingPageUrl(e.target.value)}
            placeholder="https://example.com/savings"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
          />
          <p className="mt-1.5 text-xs text-gray-500">
            Where this ad links to. If a required disclosure is missing from the ad creative
            but present on the landing page, the finding will be marked as satisfied —
            backing the &ldquo;click + scroll away&rdquo; interpretation.
          </p>
        </div>
      )}

      {/* Batch name (if multiple files) */}
      {files.length > 1 && (
        <div className="mb-6">
          <label htmlFor="batchName" className="block text-sm font-medium text-gray-700 mb-2">
            Batch Name (optional)
          </label>
          <input
            id="batchName"
            type="text"
            value={batchName}
            onChange={(e) => setBatchName(e.target.value)}
            placeholder="e.g., Q1 2024 Campaigns"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
          />
        </div>
      )}

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all ${
          isDragActive
            ? 'border-ocean-500 bg-ocean-50 shadow-lg'
            : 'border-gray-300 hover:border-ocean-400 hover:bg-gray-50'
        }`}
      >
        <input {...getInputProps()} />
        <CloudArrowUpIcon className={`mx-auto h-12 w-12 transition-colors ${isDragActive ? 'text-ocean-500' : 'text-gray-400'}`} />
        <p className="mt-2 text-sm font-medium text-navy-900">
          {isDragActive ? 'Drop files here' : 'Drag & drop files here, or click to select'}
        </p>
        <p className="mt-1 text-xs text-gray-500">
          Images, PDFs, or text files (max 50MB per file)
        </p>
      </div>

      {/* File list */}
      <AnimatePresence>
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-6"
          >
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              Selected Files ({files.length})
            </h3>
            <ul className="space-y-2">
              {files.map((file, index) => (
                <motion.li
                  key={`${file.name}-${file.size}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center space-x-3">
                    <DocumentIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload button */}
      {files.length > 0 && (
        <div className="mt-6 flex gap-4">
          <button
            onClick={handleUpload}
            disabled={uploadMutation.isPending || needsTeamSelection}
            className="flex-1 bg-ocean-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-ocean-600 focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
          >
            {uploadMutation.isPending
              ? 'Uploading...'
              : needsTeamSelection
              ? 'Loading bank…'
              : `Upload & Check Compliance (${files.length} file${files.length > 1 ? 's' : ''})`}
          </button>
          <button
            onClick={() => setFiles([])}
            className="px-6 py-3 rounded-lg font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all"
          >
            Clear All
          </button>
        </div>
      )}

      {/* Info section */}
      <div className="mt-12 bg-ocean-50 border border-ocean-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-navy-900 mb-2">
          What happens after upload?
        </h3>
        <ul className="text-sm text-navy-700 space-y-1">
          <li>• Files are securely stored in AWS S3</li>
          <li>• System extracts text and applies rules-based analysis</li>
          <li>• FDIC/NCUA compliance rules are checked</li>
          <li>• Detailed report with suggestions is generated</li>
          <li>• Processing typically takes 30-60 seconds per file</li>
        </ul>
      </div>

      {/* Recent Uploads */}
      {recentReports?.reports && recentReports.reports.length > 0 && (
        <div className="mt-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-navy-900">Recent Uploads</h2>
            <button
              onClick={() => navigate('/reports')}
              className="text-sm text-ocean-600 hover:text-ocean-700 font-medium transition-colors"
            >
              View All Reports →
            </button>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {recentReports.reports.map((report: any) => (
                <li
                  key={report.id}
                  className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => {
                    if (report.status !== 'PENDING' && report.status !== 'PROCESSING') {
                      navigate(`/reports/${report.id}`);
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      <DocumentIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {report.upload?.fileName || 'Unknown file'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(report.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      {getStatusBadge(report.status)}

                      {report.status === 'PASSED' || report.status === 'FAILED' ? (
                        <div className="text-right min-w-[100px]">
                          {report.violations && report.violations.length > 0 ? (
                            <p className="text-sm font-semibold text-danger-600">
                              {report.violations.length} {report.violations.length === 1 ? 'issue' : 'issues'}
                            </p>
                          ) : (
                            <p className="text-sm font-semibold text-mint-600">
                              No issues
                            </p>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Show violations inline for completed reports */}
                  {report.status === 'FAILED' && report.violations && report.violations.length > 0 && (
                    <div className="mt-3 pl-8">
                      <div className="bg-danger-50 border border-danger-200 rounded-md p-3">
                        <p className="text-xs font-semibold text-danger-700 mb-2">Issues Detected by Rules Engine:</p>
                        <ul className="space-y-1">
                          {report.violations.slice(0, 3).map((violation: any, idx: number) => (
                            <li key={violation.ruleId || violation.ruleName || `v-${idx}`} className="text-xs text-danger-600 flex items-start">
                              <span className="mr-1">•</span>
                              <span>{violation.ruleName || violation.description || violation.category}</span>
                            </li>
                          ))}
                          {report.violations.length > 3 && (
                            <li className="text-xs text-danger-700 font-medium">
                              +{report.violations.length - 3} more issues
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  )}

                  {report.status === 'PASSED' && (
                    <div className="mt-3 pl-8">
                      <div className="bg-mint-50 border border-mint-200 rounded-md p-3">
                        <p className="text-xs text-mint-700 font-medium">
                          No automated violations detected. Ready for human review and approval.
                        </p>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
